import React from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import { navigation } from '@origin-digital/reporting-client';
import { ExperienceProvider } from '@origin-digital/features';

import { appMboxes, routes, webMboxes } from 'src/consts';
import { usePrevious } from 'src/hooks';
import { AppContextProvider } from 'src/context/AppContext';
import { isMyAccount } from 'src/utils/helpers';
import { hideNotification, updateActionBar } from 'src/utils/notification';
import { BaseScreen } from 'src/screens/BaseScreen';
import { SelectContractAccountScreen } from 'src/screens/SelectContractAccountScreen';
import { SelectPaymentMethodScreen } from 'src/screens/SelectPaymentMethodScreen';
import { ConfirmScreen } from 'src/screens/ConfirmScreen';
import { SuccessScreen } from 'src/screens/SuccessScreen';
import { useSharedAppContext } from 'src/context/SharedAppContext';

import { hasRouteChanged, scrollToTop } from './utils';
import { IProps } from './types';

export const shouldTrackNavigation = (pathname: string): boolean =>
  Object.values(routes).some((route) => pathname.includes(route));

const shouldUpdateActionBar = (prevProps: IProps, props: IProps) =>
  // we just left the selectContractAccount page
  (prevProps && prevProps.location.pathname.includes(routes.selectContractAccount)) ||
  // we just landed on the selectContractAccount page
  props.location.pathname.includes(routes.selectContractAccount);

export const App: React.SFC<IProps> = (props) => {
  const prevProps: IProps = usePrevious<IProps>(props);
  const sharedAppContext = useSharedAppContext();
  const { isNative } = sharedAppContext;
  const mboxes = isNative ? Object.values(appMboxes) : Object.values(webMboxes);

  if (hasRouteChanged(prevProps, props)) {
    const {
      location: { href, pathname },
    } = window;

    if (shouldTrackNavigation(pathname)) {
      navigation('screen', {
        currentUri: href,
        path: pathname,
      });
    }

    scrollToTop();
    hideNotification();

    if (isMyAccount() && shouldUpdateActionBar(prevProps, props)) {
      const showPropertySelector = props.location.pathname.includes(routes.selectContractAccount);
      updateActionBar({ mobilePropertySelector: showPropertySelector ? 'compact' : 'none' });
    }
  }

  return (
    <ExperienceProvider mboxes={mboxes}>
      <AppContextProvider>
        <Switch>
          {/* Select contract account */}
          <Route path={routes.selectContractAccount} component={SelectContractAccountScreen} />

          {/* Select payment */}
          <Route path={routes.selectPaymentMethod} component={SelectPaymentMethodScreen} />

          {/* Confirm */}
          <Route path={routes.confirm} component={ConfirmScreen} />

          {/* Success */}
          <Route path={routes.success} component={SuccessScreen} />

          {/* Base */}
          <Route path={routes.base} component={BaseScreen} />

          {/* Not found */}
          <Redirect to={routes.base} />
        </Switch>
      </AppContextProvider>
    </ExperienceProvider>
  );
};
